import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Award, CheckCircle2, Clock, Download, Lock, RotateCcw, XCircle } from "lucide-react";
import { apiError, studentApi } from "@/lib/student-auth";

const btnDark =
  "inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-[11px] font-semibold disabled:opacity-50";
const btnGhost =
  "inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-4 py-2.5 text-[11px] font-semibold";

function TestRunner({ item, onExit, onPassed }) {
  const [test, setTest] = useState(null);
  const [error, setError] = useState("");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [left, setLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    studentApi
      .get(`/student/tests/${item.course_slug}`)
      .then((r) => {
        setTest(r.data);
        if (r.data.time_limit_minutes) setLeft(r.data.time_limit_minutes * 60);
      })
      .catch((err) => setError(apiError(err?.response?.data?.detail, "Could not load the test")));
  }, [item.course_slug]);

  const submit = useMemo(
    () => async (auto = false) => {
      setBusy(true);
      try {
        const { data } = await studentApi.post(`/student/tests/${item.course_slug}/submit`, { answers });
        setResult(data);
        if (data.passed) {
          toast.success("Certificate awarded!");
          onPassed();
        } else if (auto) {
          toast.error("Time is up — test submitted");
        }
      } catch (err) {
        toast.error(apiError(err?.response?.data?.detail, "Could not submit the test"));
      } finally {
        setBusy(false);
      }
    },
    [answers, item.course_slug, onPassed],
  );

  useEffect(() => {
    if (left === null || result) return;
    if (left <= 0) {
      submit(true);
      return;
    }
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [left, result, submit]);

  if (error) {
    return (
      <div data-testid="test-error" className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-6">
        <p className="text-sm text-rose-500">{error}</p>
        <button type="button" onClick={onExit} className={`${btnGhost} mt-4`} data-testid="test-error-back">
          Back to certification
        </button>
      </div>
    );
  }

  if (!test) return <div className="text-sm text-slate-400">Loading test…</div>;

  if (result) {
    return (
      <div
        data-testid="test-result"
        className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-6 text-center"
      >
        {result.passed ? (
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500" />
        ) : (
          <XCircle className="h-10 w-10 mx-auto text-rose-500" />
        )}
        <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">
          {result.passed ? "Congratulations! Certificate awarded." : "Not quite there yet."}
        </h3>
        <p className="mt-2 text-sm text-slate-500">
          Score: <span className="font-semibold">{result.score}%</span> ({result.correct}/{result.total}{" "}
          correct) · you need {result.passing_score}% to pass.
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <button type="button" onClick={onExit} className={btnDark} data-testid="test-result-back">
            Back to certification
          </button>
          {!result.passed && (
            <span className="text-[11px] text-slate-500">
              You can retake the test in {result.cooldown_minutes} minutes.
            </span>
          )}
        </div>
      </div>
    );
  }

  const q = test.questions[idx];
  const mm = left !== null ? String(Math.floor(left / 60)).padStart(2, "0") : null;
  const ss = left !== null ? String(left % 60).padStart(2, "0") : null;

  return (
    <div data-testid="test-runner" className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-base md:text-lg font-semibold tracking-tight">{test.title}</h3>
          <p className="text-[11px] text-slate-500">
            Question {idx + 1} of {test.question_count} · pass mark {test.passing_score}%
          </p>
        </div>
        {left !== null && (
          <span
            data-testid="test-timer"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold ${
              left < 60 ? "bg-rose-500/10 text-rose-500" : "bg-slate-100 dark:bg-white/5 text-slate-500"
            }`}
          >
            <Clock className="h-3 w-3" /> {mm}:{ss}
          </span>
        )}
      </div>

      <div className="mt-3 h-1.5 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
        <div
          className="h-full bg-[#06B6D4] transition-all duration-300"
          style={{ width: `${((idx + 1) / test.question_count) * 100}%` }}
        />
      </div>

      <p data-testid="test-question" className="mt-6 text-sm md:text-base font-medium leading-relaxed">
        {q.text}
      </p>

      <div className="mt-4 space-y-2">
        {q.options.map((opt, oi) => (
          <button
            key={oi}
            type="button"
            onClick={() => setAnswers({ ...answers, [q.id]: opt })}
            data-testid={`test-option-${oi}`}
            className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
              answers[q.id] === opt
                ? "border-[#06B6D4] bg-[#06B6D4]/10"
                : "border-slate-200 dark:border-white/10 hover:border-[#06B6D4]/50"
            }`}
          >
            <span className="font-semibold mr-2 text-slate-400">{String.fromCharCode(65 + oi)}</span>
            {opt}
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setIdx((v) => Math.max(0, v - 1))}
          disabled={idx === 0}
          className={btnGhost}
          data-testid="test-prev"
        >
          Previous
        </button>
        {idx < test.question_count - 1 ? (
          <button
            type="button"
            onClick={() => setIdx((v) => v + 1)}
            className={btnDark}
            data-testid="test-next"
          >
            Next question
          </button>
        ) : (
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={busy || Object.keys(answers).length === 0}
            className={btnDark}
            data-testid="test-submit"
          >
            {busy ? "Submitting…" : "Submit test"}
          </button>
        )}
      </div>
    </div>
  );
}

export const CertificationSection = ({ download, CertificateShare }) => {
  const [data, setData] = useState(null);
  const [taking, setTaking] = useState(null);

  const load = () =>
    studentApi
      .get("/student/certification")
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], certificates: [] }));

  useEffect(() => {
    load();
  }, []);

  if (taking) {
    return (
      <div data-testid="student-certification" className="space-y-4">
        <TestRunner
          item={taking}
          onExit={() => {
            setTaking(null);
            load();
          }}
          onPassed={load}
        />
      </div>
    );
  }

  const items = data?.items || [];
  const certs = data?.certificates || [];

  return (
    <div data-testid="student-certification" className="space-y-6">
      <div>
        <h2 className="font-display text-base md:text-lg font-semibold tracking-tight">Certification</h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Finish a course, pass its test and your certificate is issued instantly.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div
            key={it.course_slug}
            data-testid={`certification-item-${it.course_slug}`}
            className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5"
          >
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm font-semibold leading-snug">{it.course_title}</h3>
              <span className="text-[11px] font-semibold text-[#06B6D4]">{it.percent}%</span>
            </div>

            {it.certificate ? (
              <div className="mt-3 flex items-center gap-2 text-[11px] text-emerald-500 font-semibold">
                <Award className="h-3.5 w-3.5" /> Certificate earned
                {it.certificate.score != null ? ` · ${it.certificate.score}%` : ""}
              </div>
            ) : it.percent < 100 ? (
              <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
                <Lock className="h-3.5 w-3.5" /> Complete the course to unlock certification
              </div>
            ) : !it.has_test ? (
              <div className="mt-3 text-[11px] text-slate-500">
                Course complete — your certificate is being issued.
              </div>
            ) : it.cooldown_minutes > 0 ? (
              <div className="mt-3 flex items-center gap-2 text-[11px] text-amber-500">
                <RotateCcw className="h-3.5 w-3.5" /> Retake available in {it.cooldown_minutes} min
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <p className="text-[11px] text-slate-500">
                  {it.test_title} · {it.question_count} questions · pass {it.passing_score}%
                  {it.time_limit_minutes ? ` · ${it.time_limit_minutes} min` : ""}
                </p>
                <button
                  type="button"
                  onClick={() => setTaking(it)}
                  className={btnDark}
                  data-testid={`take-test-${it.course_slug}`}
                >
                  {it.attempts > 0 ? "Retake test" : "Take test"}
                </button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div
            data-testid="no-certification"
            className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-8 text-center text-sm text-slate-500 sm:col-span-2"
          >
            Your courses will appear here once you're enrolled.
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-base font-semibold tracking-tight">My certificates</h3>
        {certs.length === 0 ? (
          <div
            data-testid="no-certificates"
            className="mt-3 rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-8 text-center text-sm text-slate-500"
          >
            Pass a course test to earn your first certificate.
          </div>
        ) : (
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            {certs.map((c) => (
              <div
                key={c.cert_id}
                data-testid={`certificate-${c.cert_id}`}
                className="rounded-2xl border border-slate-200/80 dark:border-white/10 overflow-hidden bg-white dark:bg-white/[0.03]"
              >
                <div className="bg-[#0F172A] p-5 text-white">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#06B6D4] font-semibold">
                    Certificate of completion
                  </div>
                  <div className="mt-2 font-display text-xl font-semibold tracking-tight">{c.course_title}</div>
                  <div className="mt-1 text-xs text-slate-400">{c.student_name}</div>
                </div>
                <div className="p-4 text-xs text-slate-500 space-y-1">
                  <div>
                    Certificate ID:{" "}
                    <span className="font-medium text-[#0F172A] dark:text-white">{c.cert_id}</span>
                  </div>
                  <div>Issued: {new Date(c.issued_at).toLocaleDateString()}</div>
                  {c.score != null && <div>Test score: {c.score}%</div>}
                  <div className="pt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => download(`/student/certificates/${c.cert_id}/download`, `${c.cert_id}.pdf`)}
                      data-testid={`certificate-download-${c.cert_id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-3 py-2 text-[11px] font-semibold"
                    >
                      <Download className="h-3 w-3" /> Download PDF
                    </button>
                    <CertificateShare cert={c} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
